import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import Reveal from "@/components/sites/Reveal";
import EstatePropertyCard from "@/components/sites/EstatePropertyCard";
import PreviewBadge from "@/components/sites/PreviewBadge";
import { EstateHeader, EstateFooter, EstateStickyCta } from "@/components/sites/EstateChrome";
import { useBrand, useMarket, useTenantPath, useTenantSlug } from "@/lib/brand";
import { api, mediaUrl } from "@/lib/api";
import { imageSrcSet, SIZES } from "@/lib/responsiveImage";
import { fmtPrice } from "@/lib/terms";
import { soldStreet, soldMonth } from "@/lib/agencyDetails";
import { withPreview } from "@/lib/sitePreview";
import { useVisitInterest } from "@/lib/visitInterest";

/**
 * The area guide, in the Estate theme.
 *
 * Almost everything on the design's version turns out to have real data
 * behind it, which is why this page is worth building rather than faking:
 * `/crm/public/market-report/area` returns HM Land Registry KPIs, a summary
 * line, a breakdown by property type, six years of price history, written
 * commentary and the actual recent sales. The agency's own guide prose comes
 * off `area_guides` on the brand record.
 *
 * The two sections with no data are Schools and Transport. The design shows
 * Ofsted ratings and journey times; there is no field for either, and
 * plausible-looking distances next to a real village would be invented. They
 * are not in the table of contents rather than being there and empty.
 */
export default function EstateArea({ preview = true }) {
  const { area: areaSlug } = useParams();
  const brand = useBrand();
  const market = useMarket();
  const biz = brand.business || {};
  const tenantPath = useTenantPath();
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug();
  const [items, setItems] = useState([]);
  const [report, setReport] = useState(null);
  // Whether both requests below have SETTLED, win or lose — see the note on
  // the <main> element for what moved before this existed.
  const [loaded, setLoaded] = useState(false);
  const p = (href) => withPreview(href, preview);

  // The agency's own spelling of the area, matched off their record rather
  // than title-cased from the slug: "virginia-water" is Virginia Water, and
  // an agency covering "St Albans" would otherwise get "St albans".
  const areaName = useMemo(() => {
    const fromSlug = String(areaSlug || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    const known = (brand.areas_served || []).find(
      (a) => a.toLowerCase().replace(/\s+/g, "-") === String(areaSlug || "").toLowerCase(),
    );
    return known || fromSlug;
  }, [areaSlug, brand.areas_served]);

  // Remembered for THIS VISIT only, in memory, so the home page can lead
  // with these houses if they click back to it. Nothing is stored and the
  // server is never told. See lib/visitInterest.
  const { noteArea } = useVisitInterest();
  useEffect(() => { if (areaName) noteArea(areaName); }, [areaName, noteArea]);

  useEffect(() => {
    if (!tenantKnown || !areaName) return undefined;
    let stale = false;
    setLoaded(false);
    // Both together rather than each on its own. They used to land
    // separately, which painted the page twice; and because each only wrote
    // on success, walking from one area to the next showed the PREVIOUS
    // area's listings and market report under the new area's name until the
    // slower of the two answered.
    Promise.all([
      api.get("/crm/public/properties", { params: { tenant: tenantSlug, area: areaName, limit: 12 } })
        .then(({ data }) => data.items || []).catch(() => []),
      api.get("/crm/public/market-report/area", { params: { tenant: tenantSlug, area: areaName } })
        .then(({ data }) => data).catch(() => null),
    ]).then(([list, rep]) => {
      if (stale) return;
      setItems(list);
      setReport(rep);
      setLoaded(true);
    }).catch(() => { if (!stale) setLoaded(true); });
    return () => { stale = true; };
  }, [tenantSlug, tenantKnown, areaName]);

  const guide = (brand.area_guides || {})[areaName] || "";

  // The guide's opening sentence, and everything after it.
  //
  // The hero draws the opening line as its lede and the body drew the whole
  // guide, so every area page said the same sentence twice, once over the
  // photograph and again as the first line under "Living in <area>". Split
  // once here and shared, so the two halves cannot disagree about where the
  // sentence ends.
  //
  // A guide that is a single sentence has no body left, and then there is no
  // "Living here" section at all rather than a heading over a repeat.
  const [opening, rest] = useMemo(() => {
    const g = String(guide || "").trim();
    if (!g) return ["", ""];
    // A full stop followed by whitespace and something that starts a new
    // sentence. Requiring the capital keeps "the A30 and M3." together and
    // stops an abbreviation mid-sentence from ending it.
    const m = /^([\s\S]+?[.!?])\s+([A-Z£0-9][\s\S]*)$/.exec(g);
    return m ? [m[1], m[2]] : [g, ""];
  }, [guide]);
  const heroImage = brand.assets?.hero_image_url;
  const kpis = report?.kpis || [];
  const sales = report?.sales || [];
  const types = report?.property_types || [];
  const paragraphs = report?.commentary?.paragraphs || [];
  const insights = report?.commentary?.insights || [];

  // Only the sections that have something in them, so the contents list
  // never points at an empty heading.
  const sections = [
    rest ? { id: "living", label: "Living here" } : null,
    (paragraphs.length || types.length) ? { id: "market", label: "The market" } : null,
    sales.length ? { id: "sold", label: "Recent sales" } : null,
    items.length ? { id: "forsale", label: "For sale now" } : null,
  ].filter(Boolean);

  return (
    <div className="fs" data-testid="estate-area">
      <SeoHead
        title={`Living in ${areaName}: area guide, house prices and homes for sale | ${biz.name}`}
        description={report?.summary_line
          || (guide ? String(guide).slice(0, 180)
            : `${biz.name} are estate agents in ${areaName}. Live listings, sold prices and local knowledge.`)}
        image={heroImage ? mediaUrl(heroImage) : undefined}
      />
      <a className="skip" href="#main">Skip to content</a>
      <EstateHeader dark preview={preview} />

      {/* Same reservation as the home and listings pages, for the same
          reason measured on this one: the facts strip is 268px and inserts
          ABOVE the article, and the article sat at y=580 in an 812px
          viewport, so it was on screen and got pushed most of the way off
          it. CLS 0.093, identical on every run.

          Blocking every web font left that 0.093 exactly where it was,
          which is how it was ruled out as a type problem: Lighthouse had
          named "Web font loaded" against it, and was wrong.

          The hero holds its own height (62vh of min-height, with the
          content inside it to spare), so it could paint earlier — but its
          image comes off the brand too, so waiting for the brand costs it
          nothing and saves guessing about a long area guide's first
          sentence pushing it past the minimum. */}
      <main id="main" style={{ minHeight: "100vh" }}>
        {brand.brand_resolved && (
          <section className="area-hero">
            {heroImage && (
              <img src={mediaUrl(heroImage)} alt=""
                   srcSet={imageSrcSet({ url: heroImage, widths: brand.assets?.hero_widths })}
                   sizes={SIZES.full}
                   fetchPriority="high" decoding="async" />
            )}
            <div className="scrim" />
            <div className="container">
              <ol className="crumbs" style={{ color: "rgba(255,255,255,.75)" }}>
                <li><Link to={p(tenantPath(""))}>Home</Link></li>
                <li><Link to={p(tenantPath("guides"))}>Areas</Link></li>
                <li>{areaName}</li>
              </ol>
              <div className="eyebrow" style={{ color: "var(--accent)" }}>
                Area guide{report?.data_scope_name && report.data_scope_name !== areaName
                  ? ` · ${report.data_scope_name}` : ""}
              </div>
              <h1 className="display-xl" style={{ marginTop: 18, color: "#fff" }}>{areaName}</h1>
              {opening && (
                <p className="lede" style={{ marginTop: 18, color: "rgba(255,255,255,.85)" }}>
                  {opening}
                </p>
              )}
            </div>
          </section>
        )}

        {brand.brand_resolved && loaded && (<>
          {kpis.length > 0 && (
            <section className="section tight">
              <div className="container facts" data-testid="estate-area-facts">
                {kpis.slice(0, 4).map((k) => (
                  <div className="fact" key={k.label}>
                    <div className="v num">{k.value}</div>
                    <div className="k">{k.label}{k.delta ? ` · ${k.delta}` : ""}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="section">
            <div className="container article">
              {sections.length > 1 && (
                <nav className="toc" aria-label="On this page">
                  {sections.map((s) => <a key={s.id} href={`#${s.id}`}>{s.label}</a>)}
                </nav>
              )}

              <div>
                {rest && (
                  <Reveal as="section" id="living" className="prose">
                    <h2>Living in {areaName}</h2>
                    {rest.split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)}
                  </Reveal>
                )}

                {(paragraphs.length > 0 || types.length > 0) && (
                  <Reveal as="section" id="market" className="prose" style={{ maxWidth: "none" }}>
                    <h2>The market</h2>
                    {report?.summary_line && <p>{report.summary_line}</p>}
                    {paragraphs.map((para, i) => <p key={i}>{para}</p>)}
                    {insights.length > 0 && (
                      <ul className="features" style={{ marginTop: 18 }}>
                        {insights.map((ins, i) => <li key={i}>{ins}</li>)}
                      </ul>
                    )}
                    {types.length > 0 && (
                      <table className="sold-table" style={{ marginTop: 28 }}>
                        <thead>
                          <tr><th>Type</th><th>Sales</th><th>Share</th><th>Average</th></tr>
                        </thead>
                        <tbody>
                          {types.map((t) => (
                            <tr key={t.type}>
                              <td>{t.type}</td>
                              <td className="num">{t.count}</td>
                              <td className="num">{t.pct}%</td>
                              <td className="p num">{t.avg_price ? fmtPrice(t.avg_price, market) : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </Reveal>
                )}

                {sales.length > 0 && (
                  <Reveal as="section" id="sold" className="prose" style={{ maxWidth: "none" }}>
                    <h2>Recent sales</h2>
                    <table className="sold-table" data-testid="estate-sold-table">
                      <thead>
                        <tr><th>Street</th><th>Type</th><th>Sold</th><th>Price</th></tr>
                      </thead>
                      <tbody>
                        {sales.slice(0, 12).map((s, i) => (
                          <tr key={i}>
                            <td>{soldStreet(s.address)}</td>
                            <td>{s.property_type}</td>
                            <td className="num">{soldMonth(s.date)}</td>
                            <td className="p num">{fmtPrice(s.price, market)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {report?.methodology && (
                      <p className="small" style={{ marginTop: 16 }}>{report.methodology}</p>
                    )}
                    <p className="small">
                      Contains HM Land Registry data &copy; Crown copyright and database right.
                      This data is licensed under the Open Government Licence v3.0.
                    </p>
                  </Reveal>
                )}
              </div>
            </div>
          </section>

          {items.length > 0 && (
            <section className="section tint" id="forsale">
              <div className="container">
                <Reveal className="section-head">
                  <div>
                    <div className="eyebrow">For sale now</div>
                    <h2 className="display-m" style={{ marginTop: 14 }}>
                      On the market in <span className="serif-i">{areaName}</span>
                    </h2>
                  </div>
                  <Link className="link" to={p(tenantPath("properties"))}>
                    All properties <span className="arrow" aria-hidden="true">&rarr;</span>
                  </Link>
                </Reveal>
                <div className="grid cols-3">
                  {items.slice(0, 6).map((l) => (
                    <EstatePropertyCard key={l.id} property={l} market={market}
                                        href={p(`${tenantPath("properties")}/${l.website_slug || l.id}`)} />
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="section cta on-ink">
            <div className="container">
              <div>
                <div className="eyebrow">Thinking of moving in {areaName}?</div>
                <h2 className="display-l" style={{ marginTop: 18 }}>
                  Start with an <span className="serif-i">honest number</span>.
                </h2>
              </div>
              <div className="cta-actions">
                <Link className="btn" to={p(tenantPath("valuation"))}>
                  Value my home <span className="arrow" aria-hidden="true">&rarr;</span>
                </Link>
                <Link className="btn ghost" to={p(tenantPath("register"))}>Register as a buyer</Link>
              </div>
            </div>
          </section>
        </>)}
      </main>

      <EstateFooter preview={preview} />
      <EstateStickyCta preview={preview} />
      {preview && <PreviewBadge />}
    </div>
  );
}
